import { useMemo, useEffect, useCallback } from 'react'
import { ApolloError } from '@apollo/client'
import { ISortOrderEnum } from 'types/graphqlTypes'
import {
  formatStringListToQueryVariable,
  formatTrailerData,
  getIsShipmentDropValue,
  ON_HOLD_TO_DELIVERED_STATES,
} from 'apps/marketplace/utils/helpers/trailers'
import { SORT_VALUES_KNOWN_TRAILERS, SORT_VALUES_MISSING_TRAILERS } from '../constants'
import {
  ICarrierTrailersQueryVariables,
  useCarrierTrailersLazyQuery,
} from '../graphql/CarrierTrailers'
import {
  IMissingTrailersQueryVariables,
  useMissingTrailersLazyQuery,
} from '../graphql/MissingTrailers'
import { TCurrentFilters } from '../types'

export const getCarrierTrailersVariables = (
  currentFilters: TCurrentFilters,
  carrierId: string
): ICarrierTrailersQueryVariables => {
  const {
    after,
    before,
    deliveryDate,
    facilities,
    first,
    last,
    loadTypes,
    pickupDate,
    shipmentIds,
    shipmentStates,
    shipperIds,
    sortBy = '',
    sortOrder,
    trailerNumbers,
    trailerStates,
  } = currentFilters

  return {
    shipperIds,
    carrierId,
    shipmentIds: formatStringListToQueryVariable(shipmentIds),
    numbers: formatStringListToQueryVariable(trailerNumbers),
    shipperFacilityUuids: facilities,
    states: trailerStates || [
      'assigned_to_facility',
      'assigned_to_shipment',
      'empty_at_delivery',
      'inactive',
      'unassigned',
    ],
    ...(pickupDate && { pickupDate }),
    ...(deliveryDate && { deliveryDate }),
    shipmentStates,
    isShipmentDrop: getIsShipmentDropValue(loadTypes),
    sortBy: SORT_VALUES_KNOWN_TRAILERS[sortBy] || sortBy,
    sortDirection: (sortOrder as ISortOrderEnum) || ISortOrderEnum.DESC,
    after,
    before,
    first,
    last,
  }
}

export const getMissingTrailersVariables = (currentFilters: TCurrentFilters, carrierId: string) => {
  const {
    after,
    before,
    deliveryDate,
    facilities,
    first,
    last,
    pickupDate,
    shipmentIds,
    shipmentStates,
    shipperIds,
    sortBy = '',
    sortOrder,
  } = currentFilters

  const variables: IMissingTrailersQueryVariables = {
    after,
    before,
    sortBy: SORT_VALUES_MISSING_TRAILERS[sortBy] || sortBy,
    sortDirection: (sortOrder || ISortOrderEnum.DESC) as ISortOrderEnum,
    first,
    last,
    shipperApplicationIds: shipperIds,
    shipmentIds: formatStringListToQueryVariable(shipmentIds),
    shipperFacilityUuids: facilities,
    dropTrailer: true,
    hasTrailer: false,
    ...(pickupDate && { pickupDate }),
    ...(deliveryDate && { deliveryDate }),
    carrierApplicationIds: [carrierId],
    states: shipmentStates || ON_HOLD_TO_DELIVERED_STATES,
  }
  return variables
}

/**
 * This handles querying loads with known or unknown trailer numbers.
 * It uses the trailers query to get known trailers and the shipments query to get drop
 * shipments that are assigned to this carrier but do not have a trailer assigned.
 */
export const useCarrierTrailers = (filters: TCurrentFilters, carrierId: string) => {
  const { showMissingTrailers } = filters

  const [
    getTrailers,
    { data: carrierTrailersData, error: carrierTrailersError, loading: carrierTrailersLoading },
  ] = useCarrierTrailersLazyQuery()

  const [
    getMissingTrailers,
    { data: missingTrailersData, error: missingTrailersError, loading: missingTrailersLoading },
  ] = useMissingTrailersLazyQuery()

  const refresh = useCallback(() => {
    if (showMissingTrailers) {
      const missingTrailersVariables = getMissingTrailersVariables(filters, carrierId)
      getMissingTrailers({
        variables: missingTrailersVariables,
        context: {
          fetchPolicy: 'network-only',
        },
      })
    } else {
      const carrierTrailersVariables = getCarrierTrailersVariables(filters, carrierId)
      getTrailers({
        variables: carrierTrailersVariables,
        context: {
          fetchPolicy: 'network-only',
        },
      })
    }
  }, [getTrailers, getMissingTrailers, showMissingTrailers, carrierId, filters])

  /** Gets either missing trailers or known trailers depending on value of showMissingTrailers */
  useEffect(() => {
    if (!carrierId) return
    if (showMissingTrailers) {
      const missingTrailersVariables = getMissingTrailersVariables(filters, carrierId)
      getMissingTrailers({
        variables: missingTrailersVariables,
      })
    } else {
      const carrierTrailersVariables = getCarrierTrailersVariables(filters, carrierId)
      getTrailers({
        variables: carrierTrailersVariables,
      })
    }
  }, [getMissingTrailers, showMissingTrailers, filters, carrierId, getTrailers])

  const trailerData = useMemo(() => {
    return formatTrailerData({
      trailersData: carrierTrailersData,
      showMissingTrailers,
      missingTrailersData,
    })
  }, [missingTrailersData, carrierTrailersData, showMissingTrailers])

  const error: ApolloError | undefined = showMissingTrailers
    ? missingTrailersError
    : carrierTrailersError

  return {
    error,
    loading: carrierTrailersLoading || missingTrailersLoading,
    refresh,
    ...trailerData,
  }
}
